import { inject, Service } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

/**
 * The enumerator for the languages Vantage ships. One statement of the set: the union type, the
 * Transloco `availableLangs`, the default and every consumer's narrowing all derive from this tuple.
 * A language is added here **and** as `apps/vantage/public/i18n/<lang>.json`; `provide-transloco.spec.ts`
 * asserts every entry resolves to that asset path (the files' key parity is the `i18n-keys` gate's
 * job, and it still names the two paths literally). Mirrors `THEME_ORDER` in `provide-theme.ts`.
 */
export const SUPPORTED_LANGS = ['en', 'es'] as const;

/** One of {@link SUPPORTED_LANGS}. */
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/** The language used when nothing is stored and when a stored/active value is not one we ship. */
export const DEFAULT_LANG: SupportedLang = 'en';

/** Narrows an arbitrary string (storage, Transloco's `string`-typed active lang) to {@link SupportedLang}. */
export function isSupportedLang(value: string | null | undefined): value is SupportedLang {
  return SUPPORTED_LANGS.includes(value as SupportedLang);
}

/**
 * Reflects the active language onto `<html lang>` (WCAG 3.1.1/3.1.2 — the document language must
 * match the rendered language), mirroring how `provide-theme.ts` reflects the theme preference
 * onto `<html data-theme>`. Swallows DOM errors so SSR / a torn-down document never throws.
 */
function applyDocumentLang(lang: SupportedLang): void {
  try {
    document.documentElement.lang = lang;
  } catch {
    /* SSR */
  }
}

/** The storage key the preference is persisted under. */
const LANG_STORAGE_KEY = 'vantage_lang';

/**
 * Read the persisted language, mirroring `getStoredThemePreference` in `provide-theme.ts`: the
 * stored value is NARROWED against {@link SUPPORTED_LANGS} rather than asserted into the union, and
 * the read is wrapped because `localStorage` THROWS (it does not return `null`) when site data is
 * blocked — Safari private mode, an embedded webview. This runs inside `provideAppInitializer`, so
 * an escaping throw rejects `bootstrapApplication` and the app renders nothing at all.
 */
function readStoredLang(): SupportedLang {
  try {
    const raw = localStorage.getItem(LANG_STORAGE_KEY);
    if (isSupportedLang(raw)) return raw;
  } catch {
    /* storage unavailable (blocked site data / SSR) */
  }
  return DEFAULT_LANG;
}

/** Persist the preference. Swallows storage errors, like `storeThemePreference`. */
function storeLang(lang: SupportedLang): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

@Service()
export class LanguageService {
  readonly #transloco = inject(TranslocoService);

  /** Call once at app startup to restore the user's saved preference. */
  init(): void {
    const saved = readStoredLang();
    this.#transloco.setActiveLang(saved);
    applyDocumentLang(saved);
  }

  setLang(lang: SupportedLang): void {
    this.#transloco.setActiveLang(lang);
    storeLang(lang);
    applyDocumentLang(lang);
  }

  /**
   * The active language, narrowed to the set we ship — Transloco types `getActiveLang()` as
   * `string`, which is why both shell consumers used to hand-roll the comparison.
   */
  get activeLang(): SupportedLang {
    const active = this.#transloco.getActiveLang();
    return isSupportedLang(active) ? active : DEFAULT_LANG;
  }
}
